import type React from 'react';
import { useEffect, useId, useRef } from 'react';

export interface MeetingSwitchDialogProps {
  /** The room the user clicked, shown so they can tell which meeting they'd join. */
  meetingId: string;
  /** Leave the current meeting and move this window to the clicked room. */
  onConfirm: () => void;
  /** Stay in the current meeting. */
  onCancel: () => void;
}

/**
 * Asks whether to leave the meeting running in this window for one the user
 * clicked elsewhere in TeamHub (a Chat message, a calendar card).
 *
 * Only one meeting window exists at a time, so joining the new room means
 * leaving this one — worth confirming rather than doing silently, since the
 * click may have been aimed at a meeting for later.
 */
const MeetingSwitchDialog: React.FC<MeetingSwitchDialogProps> = ({
  meetingId,
  onConfirm,
  onCancel,
}) => {
  const headingId = useId();
  const descriptionId = useId();
  // Cancel is the safe default, so it takes focus rather than the action that
  // ends the call the user is currently in.
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    cancelButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onCancel();
      }
    };
    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [onCancel]);

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby={headingId}
      aria-describedby={descriptionId}
    >
      <div className="meeting-switch-dialog">
        <div className="meeting-switch-dialog__content">
          <h2 id={headingId} className="meeting-switch-dialog__title">
            Leave your current meeting?
          </h2>
          <p id={descriptionId} className="meeting-switch-dialog__message">
            You are about to leave this meeting and join <strong>{meetingId}</strong>.
          </p>
          <div className="meeting-switch-dialog__actions">
            <button
              ref={cancelButtonRef}
              type="button"
              className="meeting-switch-dialog__button meeting-switch-dialog__button--tertiary"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              type="button"
              className="meeting-switch-dialog__button meeting-switch-dialog__button--primary"
              onClick={onConfirm}
            >
              Join new meeting
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export { MeetingSwitchDialog };
